/*************************************
 *  api client
 ************************************/
import axios, { AxiosInstance } from 'axios';
import { camelizeKeys, decamelizeKeys } from 'humps';
import type { APIError } from '../types/app.types';
import { API_CLIENT_URL, VERSION, PROD } from '../config';

declare global {
  interface Window {
    newrelic: any;
  }
}

type PlatfromError = {
  title: string;
  message: string;
};

export const blinkAPIClient: AxiosInstance = axios.create({
  baseURL: API_CLIENT_URL,
  timeout: 20000,
  withCredentials: true,

  /* trasnsform reponse data sent to the server from under_scores to camelCase */
  transformResponse: [(data) => (data === '' ? data : camelizeKeys(JSON.parse(data)))],
  params: {
    c_platform: 'mobile_web',
    c_app: 'rx',
    c_version: VERSION,
  },
});

/* trasnsform requests data sent to the server from camelCase to under_scores */
blinkAPIClient.interceptors.request.use(
  (config) => {
    config.data = decamelizeKeys(config.data);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
// Here we handle backend api error inconcistencies
blinkAPIClient.interceptors.response.use(
  // valid responses are not transformed
  (resp) => resp, // errors are cleaned up.
  (error) => {
    // default values
    let message = 'Something went wrong, please try again';
    let validationErrors = null;
    let status = null;
    let code = null;
    let errorDetails = {};
    let requestURL = '';
    const platformError: PlatfromError = { title: '', message: '' };
    if (/^timeout.*exceeded$/.test(error && error.message)) status = 504;

    if (error.response && error.response.data) {
      const { data } = error.response;
      // HACK(leo): If data.error is an obj, this is a 'v2-style' error and should be consistent.
      // Remove this after we force update clients and send V2ErrorResponseSchema from backend.
      const dataError = typeof data.error === 'object' ? data.error : data;
      if (typeof dataError.error === 'string') message = dataError.error;
      if (dataError.errorDetails) errorDetails = dataError.errorDetails;
      if (dataError.message && typeof dataError.message === 'string') message = dataError.message;
      if (dataError.code && typeof dataError.code === 'string') code = dataError.code;
      if (error.response.status) status = error.response.status;

      if (
        dataError.platformTitle &&
        dataError.platformMessage &&
        Object.keys(dataError.platformTitle).length !== 0 &&
        Object.keys(dataError.platformMessage).length !== 0
      ) {
        platformError.title = dataError.platformTitle.mobileWeb || dataError.platformTitle.default;
        platformError.message = dataError.platformMessage.mobileWeb || dataError.platformMessage.default;
      }

      if (dataError.validationErrors !== null && typeof dataError.validationErrors === 'object') {
        validationErrors = Object.keys(dataError.validationErrors).reduce((acc, key) => {
          if (Array.isArray(dataError.validationErrors[key])) {
            acc[key] = dataError.validationErrors[key][0];
          } else {
            acc[key] = dataError.validationErrors[key];
          }

          return acc;
        }, {});
      }

      requestURL = `${error.response.config.baseURL || ''}${error.response.config.url}`;
    }

    const apiError: APIError = {
      isCancel: axios.isCancel(error),
      error: true,
      code,
      message,
      validationErrors,
      status,
      errorDetails,
      platformError,
    };

    if (PROD && status && status >= 400) {
      errorHandler(apiError, requestURL);
    }

    return Promise.reject(apiError);
  }
);

const logAPIClient: AxiosInstance = axios.create({
  baseURL: API_CLIENT_URL,
});

const errorHandler = (error: APIError, url: string) => {
  logAPIClient
    .post('/log', {
      error,
      pageUrl: window.location.href,
      requestUrl: url,
    })
    .catch((error) => {
      console.warn(error);
    });
};
